import parentApi from "store/parentApi"
import { convertQueryObjToString, localisedValue } from "utils"
import { dateTimeFunction } from "utils/date"
import { salesPipelineDataTransformer } from "./salesPipelineDataTransformer"
import { SORT_API_MAPPING } from "../utilis"
import { pipelineRevenueSplitTransformer } from "./pipelineRevenueSplitTransformer"
import { pipelineReportTransformer, pipelineSummaryTransformer } from "./pipelineReportTransformer"
import { forecastedProjectionTransformer } from "./forecastedProjectionTransformer"
import { overviewTransformer } from "./overviewTransformer"
import { wealthPipelineStagesTransformer } from "./wealthPipelineStagesTransformer"

const SalesPipelineApi = parentApi.injectEndpoints({
  endpoints: build => ({
    getSalesPipeline: build.query({
      query: ({
        page,
        pageOffset,
        pageIndex,
        step,
        conversionProbability,
        aiConversionProbability,
        pipelineType = "sales",
        stages,
        wealthPipelineStagesIds,
        dealValue,
        ...params
      }) => {
        let newParamObj = { ...params, pageOffset, pageIndex }
        let sortParams = newParamObj?.sort
        const getWealthPipelineStageIds = (stages, wealthPipelineStagesIds) => {
          if (!stages || !wealthPipelineStagesIds) return ""

          return stages
            .split(",")
            .map(stage => wealthPipelineStagesIds?.[stage])
            .filter(Boolean)
            .join(",")
        }
        if (params?.month) {
          const month = dateTimeFunction({ date: params.month, format: "M", timeZone: true, apiRequest: true })
          const year = dateTimeFunction({ date: params.month, format: "YYYY", timeZone: true, apiRequest: true })
          newParamObj = { ...newParamObj, month, year }
        }
        delete newParamObj["date"]
        const { allocatedTo } = newParamObj

        if (!!allocatedTo) {
          newParamObj.userIds = `${allocatedTo}`
          newParamObj.includeAllChildren = !params?.allocatedTo_withoutChildren
        }
        delete newParamObj["assignedTo_withoutChildren"]
        newParamObj = {
          ...newParamObj,
          page: pageIndex,
          ...SORT_API_MAPPING[sortParams],
        }
        delete newParamObj["pageIndex"]
        if (conversionProbability) {
          const [from, to] = conversionProbability.split(",")
          newParamObj = {
            ...newParamObj,
            conversionProbability: JSON.stringify({ from: +from, to: +to }),
          }
        }
        if (dealValue) {
          const [from, to] = dealValue.split(",")
          newParamObj = {
            ...newParamObj,
            dealValueMin: +from,
            dealValueMax: +to,
          }
        }
        if (aiConversionProbability) {
          const [from, to] = aiConversionProbability.split(",")
          newParamObj = {
            ...newParamObj,
            aiConversionProbability: JSON.stringify({ from: +from, to: +to }),
          }
        }
        if (stages && Object.keys(wealthPipelineStagesIds)?.length) {
          newParamObj.stageIds = getWealthPipelineStageIds(stages, wealthPipelineStagesIds)
        }
        delete newParamObj["sort"]
        return `/inquiryPipelines${pipelineType === "sales" ? "" : "/wealth"}?${convertQueryObjToString(newParamObj)}`
      },
      transformResponse: (response, meta, args) => salesPipelineDataTransformer(response, args),
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        //better cache handling for different filters and re fetch control when query params changes
        const { page, pageIndex, pageOffset, reset, ...nonPaginationArgs } = queryArgs || {}
        const serializedArgs = JSON.stringify(nonPaginationArgs, Object.keys(nonPaginationArgs).sort())
        return `${endpointName}_${serializedArgs}`
      },
      merge: (currentData, newData, { arg }) => {
        if (arg.reset === true || arg.reset === "true") {
          return newData
        }
        let newIndex = Object.keys(newData)[0]
        let pageIndex = Object.keys(currentData)[0]

        if (newIndex <= pageIndex) return newData
        let newObj = {}
        Object.keys(newData[newIndex]).forEach(key => {
          if (key !== "max") {
            if (newData[newIndex][key].count > 0) {
              newObj[key] = {
                ...currentData[pageIndex]?.[key],
                data: [...currentData[pageIndex]?.[key]?.data, ...newData[newIndex]?.[key]?.data],
                loadMore: newData[newIndex][key].loadMore,
              }
            } else {
              newObj[key] = {
                ...currentData[pageIndex][key],
                loadMore: newData[newIndex][key].count === 1,
              }
            }
          } else {
            newObj[key] = currentData[pageIndex]?.[key]
          }
        })
        return { [0]: newObj }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg
      },
      providesTags: ["salesPipelineListing"],
    }),
    getInternalInquiryStages: build.query({
      query: () => `/InternalInquiryStages`,
      transformResponse: data => {
        return data.map(e => {
          return {
            value: e?.id,
            label: localisedValue({ object: e, key: "name" }),
            key: e?.key,
            disabled: e?.key === "sold" || e?.key === "pre_closure",
          }
        })
      },
    }),
    addSalesPipeline: build.mutation({
      query: data => {
        return { url: `/inquiryPipelines`, method: "POST", body: data }
      },
      invalidatesTags: (result, error, args) => (!error ? ["salesPipelineListing"] : []),
    }),
    addWealthPipelineLead: build.mutation({
      query: ({ payload, leadId, id }) => {
        const closingMonthIso = dateTimeFunction({
          date: payload?.closingMonth,
          timeZone: true,
          apiRequest: true,
        })

        const body = {
          interestedProjectId: +payload?.interestedProjectId,
          stageId: +payload?.pipelineStageId,
          subStageId: +payload?.pipelineSubStageId,
          dealValue: +payload?.dealValue,
          expectedRevenue: +payload?.expectedRevenue,
          probabilityManual: +payload?.probabilityManual,
          closingMonth: closingMonthIso,
          comment: payload?.comment,
          ...(leadId && { leadId }),
        }

        const isUpdate = id != null && id !== ""

        return {
          url: `/inquiryPipelines/wealth${isUpdate ? `/${id}` : ""}`,
          method: isUpdate ? "PUT" : "POST",
          body,
        }
      },
      invalidatesTags: (result, error, args) => (!error ? ["salesPipelineListing"] : []),
    }),
    updateSalesPipeline: build.mutation({
      query: data => {
        return { url: `/inquiryPipelines/${data?.pipelineId}`, method: "PUT", body: data }
      },
      invalidatesTags: (result, error, args) => (!error ? ["salesPipelineListing"] : []),
    }),
    deleteSalesPipeline: build.mutation({
      query: data => {
        return { url: `/inquiryPipelines/${data?.pipelineId}`, method: "DELETE" }
      },
      invalidatesTags: (result, error, args) => (!error ? ["salesPipelineListing"] : []),
    }),
    getPipelineInsights: build.query({
      query: params => `inquiryPipelines/insights?${convertQueryObjToString(params)}`,
    }),
    getPipelineRevenueSplit: build.query({
      query: ({ inquiryId }) => `/inquiryPipelines/${inquiryId}/partialSplit`,
      transformResponse: res => pipelineRevenueSplitTransformer(res),
    }),
    getPipelineReport: build.query({
      query: ({
        sort,
        isLost,
        aiClosingMonth,
        page,
        pageIndex,
        pageLimit,
        allocatedTo,
        conversionProbability,
        aiConversionProbability,
        ...params
      }) => {
        let newParamObj = { ...params }
        if (params?.month) {
          const month = dateTimeFunction({ date: params.month, format: "M", timeZone: true, apiRequest: true })
          const year = dateTimeFunction({ date: params.month, format: "YYYY", timeZone: true, apiRequest: true })
          newParamObj = { ...newParamObj, month, year }
        }
        if (!!allocatedTo) {
          newParamObj.userIds = `${allocatedTo}`
          newParamObj.includeAllChildren = !params?.allocatedTo_withoutChildren
        }
        delete newParamObj["assignedTo_withoutChildren"]
        if (params?.load === "forecasted_widget") {
          if (conversionProbability) {
            const [from, to] = conversionProbability.split(",")
            newParamObj = {
              ...newParamObj,
              conversionProbability: JSON.stringify({ from: +from, to: +to }),
            }
          }
          if (aiConversionProbability) {
            const [from, to] = aiConversionProbability.split(",")
            newParamObj = {
              ...newParamObj,
              aiConversionProbability: JSON.stringify({ from: +from, to: +to }),
            }
          }
        }
        return `/reports/v2/pipeline_report?${convertQueryObjToString(newParamObj)}`
      },
      transformResponse: (response, error, arg) => pipelineReportTransformer(response, arg),
    }),
    getForcastedProjectionTable: build.query({
      query: ({ month, preferredFinancing, allocatedTo, projectIds, saleType, load, probabilityType }) => {
        const newParamObj = {
          ...(month && {
            month: dateTimeFunction({ date: month, format: "M", timeZone: true, apiRequest: true }),
            year: dateTimeFunction({ date: month, format: "YYYY", timeZone: true, apiRequest: true }),
          }),
          ...(preferredFinancing && { preferredFinancing }),
          ...(allocatedTo && { userIds: allocatedTo }),
          ...(projectIds && { projectIds }),
          ...(saleType && { saleType }),
          ...(load && { load }),
          ...(probabilityType && { probabilityType }),
        }

        return `/reports/v2/pipeline_report?${convertQueryObjToString(newParamObj)}`
      },
      transformResponse: response => forecastedProjectionTransformer(response),
    }),
    getOverViewKPITable: build.query({
      query: ({ month, preferredFinancing, projectIds, saleType, allocatedTo, load }) => {
        const newParamObj = {
          ...(month && {
            month: dateTimeFunction({ date: month, format: "M", timeZone: true, apiRequest: true }),
            year: dateTimeFunction({ date: month, format: "YYYY", timeZone: true, apiRequest: true }),
          }),
          ...(preferredFinancing && { preferredFinancing }),
          ...(allocatedTo && { userIds: allocatedTo }),
          ...(projectIds && { projectIds }),
          ...(saleType && { saleType }),
          ...(load && { load }),
        }
        return `/reports/v2/pipeline_report?${convertQueryObjToString(newParamObj)}`
      },
      transformResponse: (response, req, params) => overviewTransformer(response, req, params),
    }),
    getWealthPipelineStages: build.query({
      query: () => `/inquiryPipelines/wealth/stages`,
      transformResponse: wealthPipelineStagesTransformer,
    }),
  }),

  overrideExisting: false,
})

export const {
  useGetSalesPipelineQuery,
  useLazyGetSalesPipelineQuery,
  useGetInternalInquiryStagesQuery,
  useLazyGetInternalInquiryStagesQuery,
  useAddSalesPipelineMutation,
  useUpdateSalesPipelineMutation,
  useDeleteSalesPipelineMutation,
  useLazyGetPipelineInsightsQuery,
  useLazyGetPipelineRevenueSplitQuery,
  useGetPipelineReportQuery,
  useLazyGetPipelineReportQuery,
  useLazyGetForcastedProjectionTableQuery,
  useLazyGetOverViewKPITableQuery,
  useGetWealthPipelineStagesQuery,
  useLazyGetWealthPipelineStagesQuery,
  useAddWealthPipelineLeadMutation,
} = SalesPipelineApi
